import {
  Bell, Bot, Building2, FileDown, FileSearch, MapPin, Radar, Scale, ShieldCheck,
} from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import { useCorpusFacts, fmt, quantity } from './useCorpusFacts';
import GrowthChart from './GrowthChart';
import WorldDots from './WorldDots';
import styles from './HomePage.module.css';

/**
 * An alternative public home page (SRS #56).
 *
 * KN sent four reference sites over five days — kapture.cx, Zoho One,
 * quorum.us and klaviyo.com — all of them marketing homepages, while six SRS
 * rows sat blocked on "UI/UX guidelines". More references were not converging
 * it, so this is the direction built rather than described: a light canvas, a
 * plain declarative headline, mono labels, and the product itself in the hero
 * instead of an abstraction.
 *
 * The original dark page stays exactly as it is, on "/". This one answers at
 * "/home" so the two can be put side by side and one of them chosen.
 *
 * The hero frame is the dashboard drawn in markup rather than a screenshot:
 * a PNG goes stale the moment the product moves, cannot follow the theme, and
 * would have to be re-cut for every change. Every figure in it is a real one
 * from the live corpus.
 */

/**
 * The four steps are the product's actual order of operations, not a funnel:
 * nothing in Policyora works until a scope exists, which is why picking
 * jurisdictions is step one rather than a setting.
 */
const STEPS = [
  {
    icon: MapPin,
    tone: 'scope',
    title: 'Pick your jurisdictions',
    body: 'Choose the places you operate in. Everything after this — search, alerts, the dashboard, '
      + 'every report — is scoped to them, and nothing outside them clutters the view.',
  },
  {
    icon: Radar,
    tone: 'watch',
    title: 'Watch what moves',
    body: 'New instruments, amendments and commencement dates arrive against that scope, with an '
      + 'early-warning score per jurisdiction and a calendar of what comes into force next.',
  },
  {
    icon: ShieldCheck,
    tone: 'assess',
    title: 'Assess your own position',
    body: 'Bring the policies you already have. An assessment reads a regulation\u2019s obligations one by '
      + 'one and marks each met, partial or a gap — against your documents, not a questionnaire.',
  },
  {
    icon: FileDown,
    tone: 'report',
    title: 'Report it',
    body: 'Export the position as a report for the board, or a legal memo whose every citation has been '
      + 'checked back against the instrument it came from.',
  },
];

/** The capabilities on the dashboard itself, in the order they are read. */
const DASHBOARD_POINTS = [
  'An interactive timeline of key dates — what commences, what consults, what closes next.',
  'Jurisdiction filters that persist, so every panel on the screen answers for the same scope.',
  'Summary cards for the figures checked daily: volume in scope, status mix, what is new this week.',
  'A live map, so coverage and exposure read by country at a glance.',
];

/** Illustrative rows for the assessment frame. The obligation headings are the
 *  real duty categories an assessment produces, kept generic on purpose: the
 *  frame is drawn, so it must not claim a finding about a named law. */
const OBLIGATION_ROWS: { label: string; state: 'met' | 'partial' | 'gap' }[] = [
  { label: 'Risk-management system documented', state: 'met' },
  { label: 'Human oversight assigned and recorded', state: 'met' },
  { label: 'Technical documentation retained', state: 'partial' },
  { label: 'Post-market monitoring plan', state: 'gap' },
  { label: 'Serious-incident reporting process', state: 'gap' },
];

const AUDIENCES = [
  {
    icon: ShieldCheck,
    title: 'Compliance and risk',
    body: 'Which obligations apply, where the organisation stands against them, and what changed since '
      + 'the last board pack — without reading every instrument twice.',
  },
  {
    icon: Scale,
    title: 'Legal and policy counsel',
    body: 'Memos drafted from the instruments themselves, with each citation verified against the text it '
      + 'was drawn from and refused when it cannot be.',
  },
  {
    icon: Building2,
    title: 'Public sector and regulators',
    body: 'How comparable jurisdictions are legislating, side by side, with briefings that carry the '
      + 'evidence rather than summarising it away.',
  },
];

const FAQS = [
  {
    q: 'Which jurisdictions and materials are covered?',
    a: 'Federal, state and national jurisdictions across the legislative and regulatory branches — bills, '
      + 'acts, rules, guidance, executive orders, hearings and public comments.',
  },
  {
    q: 'Can alerts, summaries and analyses be customised?',
    a: 'Yes. You control the alert criteria, the summary format, the analysis instructions and the memo '
      + 'template, per workspace.',
  },
  {
    q: 'How does it know where we stand?',
    a: 'An assessment reads a regulation\u2019s obligations and checks each one against the policies you '
      + 'upload, marking it met, partial or a gap and attaching the passage it relied on.',
  },
  {
    q: 'Is there a free plan?',
    a: 'Yes — one jurisdiction, no card required. Paid plans widen the scope, add seats, assessments and '
      + 'reporting; the enterprise and government tiers add SSO and unlimited jurisdictions.',
  },
  {
    q: 'Do you support structured pilots?',
    a: 'Yes. A pilot lets your team validate the output against an existing workflow before committing.',
  },
];




/**
 * Counts a figure up on first paint.
 *
 * A number that lands is more convincing than one that is simply there — but
 * only as decoration, so a viewer who has asked for less motion gets the final
 * value immediately rather than a slower page.
 */
function useCountUp(target: number | null | undefined, ms = 1100): number | null {
  const [value, setValue] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (target == null) { setValue(null); return; }
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setValue(target); return; }
    let raf = 0;
    const started = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - started) / ms);
      // ease-out, so it decelerates into the real figure
      setValue(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return value;
}

/**
 * Sections arrive as they are scrolled to.
 *
 * One observer for the page rather than a ref per section. Anything it cannot
 * do — no IntersectionObserver, or a viewer who has asked for less motion — it
 * does not do at all: every element is marked shown immediately, so the page
 * never depends on script to be readable.
 */
function useReveal(deps: unknown[]) {
  React.useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!nodes.length) return;
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => { n.dataset.shown = 'true'; });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        (e.target as HTMLElement).dataset.shown = 'true';
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    nodes.forEach((n) => { if (n.dataset.shown !== 'true') io.observe(n); });
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}


export const HomePage: React.FC = () => {
  const { stats, top, codes, byCountry, blocs, trend, plans } =
    useCorpusFacts('Policyora — AI regulation, tracked and assessed');

  useReveal([plans.length, trend.length, Object.keys(byCountry).length]);

  const busiest = top[0]?.count || 1;
  const countRegs = useCountUp(stats?.regulations);
  const countJur = useCountUp(stats?.jurisdictions);
  const countCountries = useCountUp(stats?.countries);

  return (
    <div className={styles.page}>
      {/* One dark ground from the nav to the bottom of the figures, so the page
          opens on a statement rather than on a white rectangle. */}
      <header className={styles.topBar}>
        <div className={styles.wrap}>
          <nav className={styles.nav}>
          <img src="/Policy Ora Final Logo.png" alt="Policyora" className={styles.navLogo} />
          <span className={styles.navSpacer} />
          <Link to="/pricing" className={styles.navLink}>Pricing</Link>
          <Link to="/faq" className={styles.navLink}>FAQs</Link>
          <Link to="/login" className={styles.navLink}>Log in</Link>
          <Link to="/signup" className={styles.ctaPrimary}>Start free</Link>
          </nav>
        </div>
      </header>

      <main>
        <section className={styles.heroBand}>
          <div className={styles.aurora} aria-hidden="true">
            <span className={`${styles.orb} ${styles.orbA}`} />
            <span className={`${styles.orb} ${styles.orbB}`} />
          </div>

        <div className={styles.wrap}>
          <div className={`${styles.hero} ${styles.heroGlow}`}>
            <div className={styles.rise}>
              <p className={styles.eyebrow}>
                <span className={styles.eyebrowDot} aria-hidden="true" />
                AI regulation · tracked and assessed
              </p>
              <h1 className={styles.headline}>
                Every rule that applies to you.<br />
                <span className={styles.headlineAccent}>And where you stand against it.</span>
              </h1>
              <p className={styles.sub}>
                Policyora tracks AI and technology regulation across {fmt(stats?.jurisdictions)} jurisdictions,
                then checks your own policies against it — obligation by obligation, with the evidence attached.
              </p>
              <div className={styles.ctaRow}>
                <Link to="/signup" className={styles.ctaPrimary}>Start free</Link>
                <Link to="/pricing" className={styles.ctaSecondary}>See plans</Link>
              </div>
            </div>

            {/* The product, drawn rather than photographed. */}
            <div className={`${styles.frameStack} ${styles.rise2}`}>
            <div className={styles.frame} aria-hidden="true">
              <div className={styles.frameBar}>
                <span className={styles.dot} /><span className={styles.dot} /><span className={styles.dot} />
                <span className={styles.frameTitle}>Your coverage</span>
              </div>
              <div className={styles.frameBody}>
                <div className={styles.tiles}>
                  <div className={styles.tile}>
                    <div className={styles.tileValue}>{fmt(stats?.regulations)}</div>
                    <div className={styles.tileLabel}>In scope</div>
                  </div>
                  <div className={styles.tile}>
                    <div className={styles.tileValue}>{fmt(stats?.jurisdictions)}</div>
                    <div className={styles.tileLabel}>Jurisdictions</div>
                  </div>
                  <div className={styles.tile}>
                    <div className={styles.tileValue}>{fmt(stats?.countries)}</div>
                    <div className={styles.tileLabel}>Countries</div>
                  </div>
                </div>

                <div className={styles.frameCols}>
                  <div className={styles.frameCard}>
                    <p className={styles.frameCardTitle}>By jurisdiction</p>
                    {(top.length ? top : [{ name: ' ', count: 1 }]).map((j) => (
                      <div key={j.name} className={styles.barRow}>
                        <div className={styles.barTop}>
                          <span>{j.name}</span><span>{fmt(j.count)}</span>
                        </div>
                        <div className={styles.barTrack}>
                          <div className={styles.barFill} style={{ width: `${Math.max(4, (j.count / busiest) * 100)}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className={styles.frameCard}>
                    <p className={styles.frameCardTitle}>Status mix</p>
                    <div className={styles.donutWrap}>
                      <div
                        className={styles.donut}
                        style={{
                          background:
                            'conic-gradient(var(--ds-success, #12b76a) 0 32%, var(--ds-warning, #f79009) 32% 78%, var(--ds-border-strong, #d0d5dd) 78% 100%)',
                        }}
                      >
                        <div className={styles.donutHole}>
                          <div className={styles.donutInner}>{fmt(stats?.regulations)}</div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.legend}>
                      <span className={styles.legendItem}><span className={styles.swatch} style={{ background: 'var(--ds-success, #12b76a)' }} />In force</span>
                      <span className={styles.legendItem}><span className={styles.swatch} style={{ background: 'var(--ds-warning, #f79009)' }} />Proposed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* The verdict is the part a frame of figures cannot convey: the
                product does not only track the law, it says where you stand. */}
            <div className={styles.floatCard} aria-hidden="true">
              <span className={styles.floatTop}><span className={styles.floatDot} />Assessment complete</span>
              <span className={styles.floatBody}>24 of 31 obligations met · 7 gaps, with the evidence attached</span>
            </div>
            </div>
          </div>

        </div>

        <div className={styles.wrap}>
          <div className={styles.stats}>
            <div>
              <div className={styles.statValue}>{fmt(countRegs ?? stats?.regulations)}</div>
              <div className={styles.statLabel}>Regulations tracked</div>
            </div>
            <div>
              <div className={styles.statValue}>{fmt(countJur ?? stats?.jurisdictions)}</div>
              <div className={styles.statLabel}>Jurisdictions covered</div>
            </div>
            <div>
              <div className={styles.statValue}>{fmt(countCountries ?? stats?.countries)}</div>
              <div className={styles.statLabel}>Countries represented</div>
            </div>
          </div>
        </div>
        </section>

        {/* ── the corpus, on the world and over time ───────────────────── */}
        {(Object.keys(byCountry).length > 0 || trend.length > 1) && (
          <section
            className={`${styles.wrap} ${styles.section} ${styles.reveal}`}
            aria-label="Coverage"
            data-reveal
          >
            <div>
              <div className={styles.sectionHead}>
                <p className={styles.sectionEyebrow}>Coverage</p>
                <h2 className={styles.sectionTitle}>Where the law is, and how fast it is arriving.</h2>
                <p className={styles.sectionSub}>
                  Both of these are drawn from the live corpus the moment you load the page — not a
                  screenshot taken once and left to go stale.
                </p>
              </div>

              {/* The map takes the full width: at a third of it the dots were a
                  scatter rather than a world. */}
              {Object.keys(byCountry).length > 0 && (
                <WorldDots byCountry={byCountry} blocs={blocs} />
              )}

              <div className={styles.coverage}>
                {trend.length > 1 && <GrowthChart data={trend} />}
                {top.length > 0 && (
                  <div className={styles.leaders}>
                    <p className={styles.leadersHead}>Busiest jurisdictions</p>
                    {top.map((j) => (
                      <div key={j.name} className={styles.leaderRow}>
                        <span className={styles.leaderName}>{j.name}</span>
                        <span className={styles.leaderTrack}>
                          <span
                            className={styles.leaderFill}
                            style={{ width: `${Math.max(6, (j.count / busiest) * 100)}%` }}
                          />
                        </span>
                        <span className={styles.leaderCount}>{fmt(j.count)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section className={`${styles.wrap} ${styles.section} ${styles.reveal}`} aria-label="How it works" data-reveal>
          <div className={styles.sectionHead}>
            <p className={styles.sectionEyebrow}>How it works</p>
            <h2 className={styles.sectionTitle}>From the whole corpus to your position on it, in four steps.</h2>
            <p className={styles.sectionSub}>
              Nothing to install and no data to prepare first. You say where you operate; the product narrows
              to it, and keeps narrowing as the law moves.
            </p>
          </div>
          <ol className={styles.steps}>
            {STEPS.map(({ icon: Icon, tone, title, body }, i) => (
              <li key={title} className={`${styles.step} ${styles[tone]}`}>
                <span className={styles.stepNum}>{String(i + 1).padStart(2, '0')}</span>
                <span className={styles.stepIcon} aria-hidden="true"><Icon size={18} /></span>
                <h3 className={styles.stepTitle}>{title}</h3>
                <p className={styles.stepBody}>{body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className={`${styles.wrap} ${styles.section} ${styles.reveal}`} aria-label="What it does" data-reveal>
          <div className={styles.sectionHead}>
            <p className={styles.sectionEyebrow}>What it does</p>
            <h2 className={styles.sectionTitle}>A library is not the same as an answer.</h2>
            <p className={styles.sectionSub}>
              Most tools tell you what the law says. Policyora also tells you where your organisation stands
              against it, and what to do next.
            </p>
            {codes.length > 0 && (
              <div className={styles.codeGrid} aria-label={`${codes.length} jurisdictions tracked`}>
                {codes.slice(0, 40).map((c) => <span key={c} className={styles.code}>{c}</span>)}
                {codes.length > 40 && <span className={styles.codeMore}>+{codes.length - 40} more</span>}
              </div>
            )}
          </div>

          <div className={styles.bento}>
            <article className={`${styles.card} ${styles.cardLead}`}>
              <span className={`${styles.cardIcon} ${styles.assess}`}><ShieldCheck size={20} aria-hidden /></span>
              <h3 className={styles.cardTitle}>Assess your position</h3>
              <p className={styles.cardBody}>
                Governance, risk and compliance assessments read a regulation&rsquo;s obligations one at a
                time and check each against your own policies — met, partial or a gap, with the passage it
                was judged on attached and the whole thing exportable as a report.
              </p>
              <div className={styles.mini} aria-hidden="true">
                <div className={styles.meter}>
                  <div className={styles.meterMet} style={{ width: '58%' }} />
                  <div className={styles.meterPartial} style={{ width: '19%' }} />
                </div>
                <div className={styles.meterKey}>
                  <span><span className={`${styles.keyDot} ${styles.keyMet}`} />18 met</span>
                  <span><span className={`${styles.keyDot} ${styles.keyPartial}`} />6 partial</span>
                  <span><span className={`${styles.keyDot} ${styles.keyGap}`} />7 gaps</span>
                  <span className={styles.meterTotal}>31 obligations</span>
                </div>
              </div>
            </article>

            <article className={styles.card}>
              <span className={`${styles.cardIcon} ${styles.scope}`}><FileSearch size={20} aria-hidden /></span>
              <h3 className={styles.cardTitle}>Search every regulation</h3>
              <p className={styles.cardBody}>
                One corpus across {fmt(stats?.countries)} countries — bills, acts, guidance and executive orders —
                filtered to the jurisdictions you operate in.
              </p>
              <div className={styles.mini} aria-hidden="true">
                <div className={styles.miniSearch}>
                  <FileSearch size={13} />
                  <span>facial recognition · public spaces</span>
                </div>
                <div className={styles.miniChips}>
                  {(codes.length ? codes.slice(0, 4) : ['EU', 'UK', 'US-CA', 'SG']).map((c) => (
                    <span key={c} className={styles.miniChip}>{c}</span>
                  ))}
                </div>
              </div>
            </article>

            <article className={styles.card}>
              <span className={`${styles.cardIcon} ${styles.watch}`}><Radar size={20} aria-hidden /></span>
              <h3 className={styles.cardTitle}>Know before it lands</h3>
              <p className={styles.cardBody}>
                Alerts on the instruments you follow, an early-warning score per jurisdiction, and a calendar of
                what comes into force next.
              </p>
              <div className={styles.mini} aria-hidden="true">
                {[
                  { when: 'in 12 days', what: 'Transparency duty commences', tone: 'soon' },
                  { when: 'in 6 weeks', what: 'Consultation closes', tone: '' },
                ].map((r) => (
                  <div key={r.what} className={styles.miniAlert}>
                    <Bell size={12} />
                    <span className={styles.miniAlertText}>{r.what}</span>
                    <span className={`${styles.miniWhen} ${r.tone ? styles.miniSoon : ''}`}>{r.when}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className={`${styles.card} ${styles.cardWide}`}>
              <span className={`${styles.cardIcon} ${styles.report}`}><Bot size={20} aria-hidden /></span>
              <h3 className={styles.cardTitle}>Ask in plain language</h3>
              <p className={styles.cardBody}>
                An assistant grounded in the corpus: it answers from the instruments themselves and cites what it
                used, so an answer can be checked.
              </p>
              <div className={styles.mini} aria-hidden="true">
                <div className={styles.miniAsk}>Does this apply to a model we only fine-tune?</div>
                <div className={styles.miniReply}>
                  <Bot size={12} />
                  <span>Yes, in two of your five jurisdictions — here is where each says so.</span>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* ── the dashboard, and the assessment it produces ───────────── */}
        <section className={`${styles.wrap} ${styles.section} ${styles.reveal}`} aria-label="Dashboard in action" data-reveal>
          <div className={styles.split}>
            <div className={styles.splitText}>
              <p className={styles.sectionEyebrow}>Dashboard in action</p>
              <h2 className={styles.sectionTitle}>Everything your team checks, on one screen.</h2>
              <p className={styles.sectionSub}>
                One scope, applied everywhere. Change the jurisdictions and every panel — volume, timeline,
                map, status mix — answers for the same set.
              </p>
              <ul className={styles.ticks}>
                {DASHBOARD_POINTS.map((point) => (
                  <li key={point} className={styles.tick}>{point}</li>
                ))}
              </ul>
              <div className={styles.ctaRow}>
                <Link to="/signup" className={styles.ctaSecondary}>See it with your own scope</Link>
              </div>
            </div>

            <div className={styles.frame} aria-hidden="true">
              <div className={styles.frameBar}>
                <span className={styles.dot} /><span className={styles.dot} /><span className={styles.dot} />
                <span className={styles.frameTitle}>Assessment · 24 of 31 met</span>
              </div>
              <div className={styles.frameBody}>
                <div className={styles.meter}>
                  <div className={styles.meterMet} style={{ width: '58%' }} />
                  <div className={styles.meterPartial} style={{ width: '19%' }} />
                </div>
                <div className={styles.obligations}>
                  {OBLIGATION_ROWS.map((row) => (
                    <div key={row.label} className={styles.oblRow}>
                      <span className={styles.oblLabel}>{row.label}</span>
                      <span className={`${styles.oblState} ${styles[row.state]}`}>
                        {row.state === 'met' ? 'Met' : row.state === 'partial' ? 'Partial' : 'Gap'}
                      </span>
                    </div>
                  ))}
                </div>
                <p className={styles.frameFoot}>
                  Each row carries the passage it was judged on, and the policy it was found in.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── the assistant, mirrored so the page does not march ───────── */}
        <section className={`${styles.darkBand} ${styles.reveal}`} aria-label="AI policy assistant" data-reveal>
          <div className={`${styles.wrap} ${styles.section} ${styles.split} ${styles.splitReverse}`}>
            <div className={styles.splitText}>
              <p className={styles.sectionEyebrow}>Assistant and search</p>
              <h2 className={styles.sectionTitle}>Ask it. Then check the answer.</h2>
              <p className={styles.sectionSub}>
                The assistant answers from the instruments in your scope and names the ones it used. An answer
                it cannot trace back to the corpus is refused rather than guessed — which is the only way a
                legal answer is worth anything.
              </p>
              <ul className={styles.ticks}>
                <li className={styles.tick}>
                  Search the text of an instrument, not only its title, with the matching passage shown.
                </li>
                <li className={styles.tick}>
                  Filters that mirror the dashboard: jurisdiction, status, sector, date of last action.
                </li>
                <li className={styles.tick}>
                  Every chat is kept, so a question asked in March can be re-run against April&rsquo;s corpus.
                </li>
              </ul>
            </div>

            <div className={styles.frame} aria-hidden="true">
              <div className={styles.frameBar}>
                <span className={styles.dot} /><span className={styles.dot} /><span className={styles.dot} />
                <span className={styles.frameTitle}>Assistant</span>
              </div>
              <div className={styles.frameBody}>
                <div className={styles.askRow}>
                  <span className={styles.askIcon}><FileSearch size={14} aria-hidden /></span>
                  <span className={styles.ask}>Which of our gaps are driven by documentation duties?</span>
                </div>
                <div className={styles.replyRow}>
                  <span className={styles.replyIcon}><Bot size={14} aria-hidden /></span>
                  <div className={styles.reply}>
                    <p className={styles.replyBody}>
                      Three of seven: technical documentation retention, the post-market monitoring plan, and
                      the serious-incident reporting process. The first is partial — a retention period is set
                      but not the register.
                    </p>
                    <div className={styles.cites}>
                      <span className={styles.cite}>Retention duty</span>
                      <span className={styles.cite}>Monitoring plan</span>
                      <span className={styles.cite}>Incident reporting</span>
                    </div>
                  </div>
                </div>
                <p className={styles.frameFoot}>Grounded in your scope · 3 sources cited</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── who it is for ────────────────────────────────────────────── */}
        <section className={`${styles.wrap} ${styles.section} ${styles.reveal}`} aria-label="Who it is for" data-reveal>
          <div className={styles.sectionHead}>
            <p className={styles.sectionEyebrow}>Who it is for</p>
            <h2 className={styles.sectionTitle}>Three jobs, one corpus.</h2>
          </div>
          <div className={styles.cards}>
            {AUDIENCES.map(({ icon: Icon, title, body }) => (
              <article key={title} className={styles.card}>
                <span className={styles.cardIcon}><Icon size={18} aria-hidden /></span>
                <h3 className={styles.cardTitle}>{title}</h3>
                <p className={styles.cardBody}>{body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ── the plans, read from the catalogue ───────────────────────── */}
        {plans.length > 0 && (
          <section className={`${styles.wrap} ${styles.section} ${styles.reveal}`} aria-label="Plans" data-reveal>
            <div className={styles.sectionHead}>
              <p className={styles.sectionEyebrow}>Plans</p>
              <h2 className={styles.sectionTitle}>Priced by how much of the world you have to watch.</h2>
              <p className={styles.sectionSub}>
                Every plan includes search, alerts and the assistant. What widens is the scope, the seats and
                the assessment and reporting work.
              </p>
            </div>
            <div className={styles.planRow}>
              {plans.map((pl) => (
                <article key={pl.tier} className={`${styles.plan} ${pl.is_popular ? styles.planPopular : ''}`}>
                  {pl.is_popular && <span className={styles.planFlag}>Most chosen</span>}
                  <h3 className={styles.planName}>{pl.display_name}</h3>
                  <p className={styles.planPrice}>
                    {pl.contact_sales
                      ? <span className={styles.planContact}>Talk to us</span>
                      : <>
                          <span className={styles.planAmount}>
                            ${Math.round(Number(pl.monthly_price_usd ?? 0))}
                          </span>
                          <span className={styles.planPer}>/month</span>
                        </>}
                  </p>
                  <p className={styles.planMeta}>
                    {quantity(pl.features?.jurisdictions, 'jurisdiction', 'jurisdictions')}
                    {' · '}
                    {quantity(pl.features?.user_seats, 'seat', 'seats')}
                  </p>
                  <Link to="/pricing" className={styles.planLink}>What is included →</Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* ── questions, with the full set a click away ────────────────── */}
        <section className={`${styles.wrap} ${styles.section} ${styles.reveal}`} aria-label="Questions" data-reveal>
          <div className={styles.faqSplit}>
            <div className={styles.sectionHead}>
              <p className={styles.sectionEyebrow}>Questions</p>
              <h2 className={styles.sectionTitle}>The ones we are asked first.</h2>
              <p className={styles.sectionSub}>
                There are more on the <Link to="/faq" className={styles.inlineLink}>FAQ page</Link>, and the
                assistant there will answer from the corpus itself.
              </p>
            </div>
            <div className={styles.faqList}>
              {FAQS.map((item) => (
                <details key={item.q} className={styles.faqItem}>
                  <summary className={styles.faqQ}>{item.q}</summary>
                  <p className={styles.faqA}>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.closingOuter}>
          <div className={`${styles.wrap} ${styles.closing}`}>
            <h2 className={styles.sectionTitle}>Start with your jurisdictions.</h2>
            <p className={styles.sectionSub}>
              Free to begin, no card required. Pick the places you operate in and the product narrows to them.
            </p>
            <div className={styles.ctaRow} style={{ justifyContent: 'center' }}>
              <Link to="/signup" className={styles.ctaPrimary}>Create an account</Link>
              <Link to="/pricing" className={styles.ctaSecondary}>Compare plans</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className={styles.footerOuter}>
        <div className={`${styles.wrap} ${styles.footerGrid}`}>
          <div className={styles.footerBrand}>
            <img src="/Policy Ora Final Logo.png" alt="Policyora" className={styles.footerLogo} />
            <p className={styles.footerBlurb}>
              AI and technology regulation across {fmt(stats?.jurisdictions)} jurisdictions, and where your
              organisation stands against it.
            </p>
          </div>
          <nav className={styles.footerCol} aria-label="Product">
            <p className={styles.footerHead}>Product</p>
            <Link to="/pricing" className={styles.footerLink}>Pricing</Link>
            <Link to="/live-updates" className={styles.footerLink}>Live updates</Link>
          </nav>
          <nav className={styles.footerCol} aria-label="Support">
            <p className={styles.footerHead}>Support</p>
            <Link to="/faq" className={styles.footerLink}>FAQs</Link>
            <a href="mailto:sales@policyora.ai" className={styles.footerLink}>sales@policyora.ai</a>
          </nav>
          <nav className={styles.footerCol} aria-label="Account">
            <p className={styles.footerHead}>Account</p>
            <Link to="/login" className={styles.footerLink}>Log in</Link>
            <Link to="/signup" className={styles.footerLink}>Start free</Link>
          </nav>
        </div>
        <div className={`${styles.wrap} ${styles.footerBase}`}>
          <span>© {new Date().getFullYear()} Policyora. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
